import React from 'react';
import SEO from '../components/shared/SEO';
import PlannerIcons from '../components/shared/PlannerIcons';

export default function EViteShowcasePage({ navigateTo }) {
    const features = [
        { icon: '✨', title: 'Smart AI Wording', desc: 'Type your party theme and watch the AI write the perfect, catchy invitation in seconds.' },
        { icon: '📱', title: 'SMS & Email Delivery', desc: 'Send invites directly to phone contacts or email addresses with one click.' },
        { icon: '📊', title: 'Real-time RSVP Dashboard', desc: 'Track dietary restrictions, potluck items, and poll results in real-time.' },
        { icon: '🎵', title: 'Spotify Integration', desc: 'Let your guests suggest songs for your event playlist directly from their invite.' },
        { icon: '📸', title: 'Live Photo Drop', desc: 'A shared gallery where guests can upload memories during the event—no app required.' },
        { icon: '☁️', title: 'Interactive Reveals', desc: 'Wow guests with physical-like digital envelopes they can open on their phones.' }
    ];

    const themes = [
        { name: 'Classic Elegance', bg: 'bg-stone-50', text: 'text-stone-900', border: 'border-stone-200', img: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=800&q=80' },
        { name: 'Rustic Charm', bg: 'bg-[#fefae0]', text: 'text-[#3a4035]', border: 'border-[#d4a373]', img: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?auto=format&fit=crop&w=800&q=80' },
        { name: 'Coastal Breeze', bg: 'bg-sky-50', text: 'text-sky-900', border: 'border-sky-200', img: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=800&q=80' },
    ];

    return (
        <div className="min-h-screen bg-white font-sans animate-fade-in pb-20">
            <SEO title="AI E-vites & Digital Invitations" description="Create stunning, interactive digital invitations with Host-it! AI E-vites." />

            {/* Hero Section */}
            <section className="relative h-[85vh] w-full flex items-center justify-center overflow-hidden px-6">
                {/* Immersive Background */}
                <div className="absolute inset-0">
                    <img 
                        src="https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1920&q=80" 
                        alt="Wedding Background" 
                        className="w-full h-full object-cover"
                    />
                    {/* Sophisticated Spotlight Overlay: darker at edges, lighter in middle */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.2)_0%,rgba(0,0,0,0.6)_100%)]"></div>
                </div>
                
                <div className="relative z-10 max-w-4xl mx-auto text-center animate-fade-in-up">
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-pink-300 text-xs font-bold uppercase tracking-widest mb-6 shadow-sm">
                        <span className="text-xl leading-none">✨</span> AI-Powered Invitations
                    </span>
                    <h1 className="text-5xl md:text-8xl font-extrabold font-serif leading-[0.95] mb-8 tracking-tighter text-white drop-shadow-2xl">
                        Invitations that <br/> <span className="text-pink-400 italic">set the tone.</span>
                    </h1>
                    <p className="text-lg md:text-2xl text-gray-100 max-w-2xl mx-auto mb-10 leading-relaxed font-light drop-shadow-md">
                        Generate stunning, interactive digital invitations in seconds. RSVPs, potlucks, and playlists—handled.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <button 
                            onClick={() => navigateTo('login')}
                            className="bg-white hover:bg-gray-100 text-gray-900 px-10 py-5 rounded-full font-bold text-lg transition-all shadow-[0_10px_40px_-10px_rgba(255,255,255,0.3)] hover:scale-105 transform"
                        >
                            Create Your Free E-vite
                        </button>
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="max-w-7xl mx-auto py-24 px-6">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-gray-900 font-serif mb-4">More than just an RSVP.</h2>
                    <p className="text-lg text-gray-500">Built-in tools to manage your event effortlessly.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {features.map((f, i) => (
                        <div key={i} className="p-8 rounded-3xl bg-gray-50 border border-gray-100 hover:shadow-lg hover:border-pink-200 transition-all duration-300 group hover:scale-[1.02]">
                            <div className="text-4xl mb-6">{f.icon}</div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">{f.title}</h3>
                            <p className="text-gray-600 leading-relaxed text-sm">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Themes Showcase */}
            <section className="py-24 bg-gray-900 text-white">
                <div className="container mx-auto px-6 max-w-7xl text-center">
                    <h2 className="text-4xl font-bold font-serif mb-16">Designer Themes</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {themes.map((theme, idx) => (
                            <div key={idx} className="rounded-2xl overflow-hidden border border-gray-700 hover:border-pink-500 transition group flex flex-col">
                                <div className="h-56 overflow-hidden relative">
                                    <img src={theme.img} alt={theme.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                                </div>
                                <div className={`p-8 flex-1 ${theme.bg}`}>
                                    <h3 className={`text-2xl font-bold font-serif mb-4 ${theme.text}`}>{theme.name}</h3>
                                    <div className={`h-1 w-12 rounded-full mx-auto ${theme.text} opacity-30`}></div>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="mt-16 text-center">
                        <p className="text-gray-400 font-medium mb-4 italic">...and 15+ more themes designed for every occasion.</p>
                        <button onClick={() => navigateTo('login')} className="bg-gray-800 hover:bg-gray-700 text-white px-8 py-3 rounded-full font-bold transition">Browse All Themes</button>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-32 bg-pink-50 text-center px-6 border-b border-pink-100">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 font-serif mb-6">Ready to start planning?</h2>
                <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">Creating an E-vite on Host-it! is free. Get your custom link and start collecting RSVPs in under 60 seconds.</p>
                <button 
                    onClick={() => navigateTo('login')}
                    className="bg-pink-600 hover:bg-pink-700 text-white px-12 py-4 rounded-full font-bold text-lg transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                >
                    Generate an E-vite
                </button>
            </section>
        </div>
    );
}